/**
 * JSON-LD 안전 직렬화
 *
 * ⛔ AD-3(2026-09-07) — 정본은 components/seo/JsonLd 다. 이 파일은 그 컴포넌트가
 *    쓰는 «이스케이프 원시함수» 로만 남는다. 페이지에서 직접 부르지 말 것.
 *
 * 문제: <script type="application/ld+json"> 안에 JSON.stringify() 결과를 그대로 넣으면
 *      만약 어떤 값이 "</script>" 문자열 포함하면 script 태그가 닫히고 XSS 가능
 *
 * 해결: '<' 를 '\u003c' 로 escape (JSON 파서는 그대로 인식, HTML 파서는 무시)
 */

export function jsonLdSafe(obj: unknown): string {
  return JSON.stringify(obj)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/'/g, '\\u0027')
    .replace(/\u2028/g, '\\u2028')  // line separator
    .replace(/\u2029/g, '\\u2029'); // paragraph separator
}

/* ─────────────────────────────────────────────────────────────────────────────
 * pruneJsonLd — 최종지시서 ㉑ §3-7 (C③ JSON-LD 빈 값).
 *
 * `components/seo/JsonLd` 가 직렬화 직전에 부른다(ld+json 의 유일한 출구 · AD-3).
 *   ① 값이 null·undefined·''(공백 포함)·NaN 인 속성은 뺀다.
 *   ② 빈 배열 속성은 뺀다. 비워진 하위 객체(@type 만 남은 것 — 예: 빈 PostalAddress)도 뺀다.
 *   ③ 원소 0 인 ItemList·BreadcrumbList 노드는 «노드째» 내지 않는다(운영 실측: 미분양 허브 ItemList []).
 *   ④ price/lowPrice/highPrice 가 0 인 Offer·AggregateOffer 는 뺀다 — 단 소프트웨어(WebApplication·
 *      SoftwareApplication·MobileApplication) 의 offers 는 «무료» 표기라 그대로 둔다.
 * ⚠️ 원본을 건드리지 않고 새 값을 돌려준다. 노드 전체가 비면 null.
 * ───────────────────────────────────────────────────────────────────────────── */
const LIST_TYPES = new Set(['ItemList', 'BreadcrumbList']);
const OFFER_TYPES = new Set(['Offer', 'AggregateOffer']);
const SOFTWARE_TYPES = new Set(['WebApplication', 'SoftwareApplication', 'MobileApplication', 'VideoGame']);

const typesOf = (o: Record<string, unknown>): string[] => {
  const t = o['@type'];
  return Array.isArray(t) ? t.map(String) : t ? [String(t)] : [];
};
const isZeroPrice = (v: unknown) => v !== null && v !== undefined && v !== '' && Number(v) === 0;

function pruneValue(v: unknown, parentIsSoftware: boolean): unknown {
  if (v === null || v === undefined) return undefined;
  if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;
  if (typeof v === 'string') return v.trim() === '' ? undefined : v;
  if (Array.isArray(v)) {
    const out = v.map((x) => pruneValue(x, parentIsSoftware)).filter((x) => x !== undefined);
    return out.length > 0 ? out : undefined;
  }
  if (typeof v === 'object') return pruneNode(v as Record<string, unknown>, parentIsSoftware) ?? undefined;
  return v;
}

function pruneNode(o: Record<string, unknown>, parentIsSoftware: boolean): Record<string, unknown> | null {
  const types = typesOf(o);
  const isSoftware = types.some((t) => SOFTWARE_TYPES.has(t));
  if (!parentIsSoftware && types.some((t) => OFFER_TYPES.has(t))) {
    if (isZeroPrice(o.price) || isZeroPrice(o.lowPrice) || isZeroPrice(o.highPrice)) return null;
  }
  const out: Record<string, unknown> = {};
  for (const [k, raw] of Object.entries(o)) {
    const v = pruneValue(raw, isSoftware);
    if (v !== undefined) out[k] = v;
  }
  if (types.some((t) => LIST_TYPES.has(t)) && !Array.isArray(out.itemListElement)) return null;
  const meaningful = Object.keys(out).filter((k) => k !== '@type' && k !== '@context');
  if (meaningful.length === 0) return null;
  return out;
}

export function pruneJsonLd<T extends Record<string, unknown>>(obj: T): T | null {
  return pruneNode(obj, false) as T | null;
}
